import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  fetchDeployedAppVersion,
  getRunningAppVersion,
  isNewerVersion,
} from "./appVersion";
import {
  hasSelfReloadingScreen,
  subscribeToSelfReloadingScreens,
} from "./selfReloadingScreens";
import { usePostHog } from "posthog-js/react";

const DEFAULT_POLLING_INTERVAL_MS = 5 * 60 * 1000;

export const useAppUpdate = ({
  pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
}: { pollingIntervalMs?: number } = {}) => {
  const [isNewVersionDeployed, setIsNewVersionDeployed] =
    useState<boolean>(false);
  const [deployedVersion, setDeployedVersion] = useState<string | null>(null);

  const postHog = usePostHog();
  const isScreenReloadingByItself = useSyncExternalStore(
    subscribeToSelfReloadingScreens,
    hasSelfReloadingScreen,
    () => false
  );

  useEffect(() => {
    if (isNewVersionDeployed) {
      return;
    }

    let stopped = false;

    const checkDeployedVersion = () => {
      fetchDeployedAppVersion()
        .then(deployedVersion => {
          if (deployedVersion) setDeployedVersion(deployedVersion);

          if (
            !stopped &&
            isNewerVersion(deployedVersion, getRunningAppVersion())
          ) {
            setIsNewVersionDeployed(true);
          }
        })
        .catch(e => console.error(e));
    };

    const checkDeployedVersionWhenVisible = () => {
      if (!document.hidden) {
        checkDeployedVersion();
      }
    };

    checkDeployedVersion();
    const intervalId = setInterval(checkDeployedVersion, pollingIntervalMs);
    document.addEventListener(
      "visibilitychange",
      checkDeployedVersionWhenVisible
    );
    window.addEventListener("focus", checkDeployedVersion);

    return () => {
      stopped = true;
      clearInterval(intervalId);
      document.removeEventListener(
        "visibilitychange",
        checkDeployedVersionWhenVisible
      );
      window.removeEventListener("focus", checkDeployedVersion);
    };
  }, [isNewVersionDeployed, pollingIntervalMs]);

  const updateApp = useCallback(() => {
    postHog.capture("app_update", {
      newVersion: deployedVersion,
      oldVersion: getRunningAppVersion(),
      automaticReload: false,
    });

    window.location.reload();
  }, [deployedVersion, postHog]);

  return {
    isUpdateAvailable: isNewVersionDeployed && !isScreenReloadingByItself,
    updateApp,
  };
};
